/**
 * Follow-Up Steps Page
 *
 * Multi-step form for editing modules.
 * - Existing users: Only show selected modules with prefilled data
 * - New users: Show remaining modules with BLANK fields (no defaults)
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useFollowUp } from '../context/FollowUpContext';
import { useTenant } from '../context/TenantContext';
import DynamicStepComponent from '../components/form/DynamicStepComponent';
import FormNavigation from '../components/form/FormNavigation';
import Medication from '../components/medication/Medication';
import Pharmacy from '../components/Pharmacy/Pharmacy';
import MedicationAllergies from '../components/MedicationAllergies/MedicationAllergies';
import Step5Diagram from '../components/form/Step5Diagram';
import { generateYupSchema } from '../utils/validationSchemas';
import { registerFieldLabels } from '../utils/fieldLabelRegistry';
import { DEFAULT_STATE } from '../utils/usStates';
import { environment } from '../env/environment';
import defaultLogo from '../assets/logo.png';
import logger from '../utils/logger';
import '../styles/formPage.css';
import '../styles/followup.css';
import * as Yup from 'yup';

// Normalize field names to match database format (lowercase, no special chars)
const convertFieldNameToLowercase = (fieldName) => {
  if (!fieldName) return '';
  // Remove all non-alphanumeric characters and convert to lowercase
  return fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const getFieldName = (field, index) => {
  if (!field) return `field_${index}`;
  if (field.name) return convertFieldNameToLowercase(field.name);
  if (field.labelName) return convertFieldNameToLowercase(field.labelName);
  if (field.label) return convertFieldNameToLowercase(field.label);
  return `field_${index}`;
};

// Progress indicator component matching Client app
// M-03: Config map for required vs optional modules in followup flow
const REQUIRED_MODULES = new Set([
  'patient onboarding details',
  'new patient onboarding',
  'terms and conditions',
  'consent',
]);

const isModuleRequired = (moduleName) => {
  if (!moduleName) return false;
  return REQUIRED_MODULES.has(moduleName.toLowerCase().trim());
};

const StepProgressIndicator = ({ currentStep, totalSteps, moduleNames }) => {
  return (
    <div className="progress-compact">
      <div className="progress-dots">
        {moduleNames.map((name, index) => {
          const stepNum = index + 1;
          const isActive = stepNum === currentStep;
          const isCompleted = stepNum < currentStep;

          return (
            <div
              key={index}
              className={`progress-dot-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              title={name}
            >
              <div className={`progress-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                {isCompleted && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                )}
              </div>
              {isActive && (
                <span className="progress-dot-label">
                  {name}
                  {/* M-03: Required/Optional pill */}
                  {isModuleRequired(name) ? (
                    <span style={{ marginLeft: '6px', fontSize: '10px', color: '#dc2626', fontWeight: 600 }}>*Required</span>
                  ) : (
                    <span className="optional-badge" style={{ marginLeft: '6px', fontSize: '10px', background: '#e5e7eb', color: '#6b7280', padding: '1px 6px', borderRadius: '8px', fontWeight: 500 }}>Optional</span>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="progress-counter">
        {currentStep} / {totalSteps}
        {/* L-02: Estimated completion time */}
        <span style={{ fontSize: '10px', color: '#9ca3af', display: 'block', marginTop: '2px' }}>
          ~{Math.max(1, Math.ceil((totalSteps - currentStep + 1) * 1.5))} min left
        </span>
      </div>
    </div>
  );
};

const FollowUpSteps = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const {
    isExistingUser,
    isNewUser,
    isLoading,
    error,
    availableModules,
    selectedModules,
    previousData,
    painDiagramData,
    currentStep,
    updateFields,
    goToNextStep,
    goToPreviousStep,
    getMergedFormData,
    setCurrentStep,
    allModules,
    setAllModules,
    setFieldsMap: setContextFieldsMap,
  } = useFollowUp();

  // Get the logo to display - use tenant logo if available, otherwise default
  const logoToDisplay = tenant?.logoUrl || defaultLogo;

  const [moduleData, setModuleData] = useState([]);
  const [fieldsMap, setFieldsMap] = useState({});
  const [moduleContainerWidths, setModuleContainerWidths] = useState({});
  const [painLevels, setPainLevels] = useState({ current: 0, worst: 0, best: 0 });
  const [painDiagramPdf, setPainDiagramPdf] = useState(null);
  const [canvasGender, setCanvasGender] = useState('woman');
  const [submitError, setSubmitError] = useState(null);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const fieldsMapFetched = useRef(false);
  const step5Ref = useRef(null);

  // Give session storage a moment to be read (runs once on mount)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSessionReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Redirect if no valid user type (only after session is ready)
  useEffect(() => {
    if (sessionReady && !isExistingUser && !isNewUser && !isLoading) {
      logger.log('[Steps] Not a valid user, redirecting to /');
      navigate('/');
    }
  }, [isExistingUser, isNewUser, isLoading, sessionReady, navigate]);

  // Set canvas gender based on previous data (for existing users)
  useEffect(() => {
    if (isExistingUser && previousData) {
      // Check for gender value in various formats
      const genderValue = previousData.gender || previousData.Gender;
      if (genderValue) {
        // Map form values (Male/Female) to canvas values (man/woman)
        const mappedGender = genderValue.toLowerCase() === 'male' ? 'man' :
                            genderValue.toLowerCase() === 'female' ? 'woman' :
                            genderValue.toLowerCase() === 'man' ? 'man' : 'woman';
        setCanvasGender(mappedGender);
      }
    }
  }, [isExistingUser, previousData]);

  // Initialize modules based on user type
  useEffect(() => {
    const initialize = async () => {
      if (isInitialized) return;

      try {
        if (isNewUser) {
          let modulesToUse = allModules;

          if (modulesToUse.length === 0) {
            const res = await fetch(`${environment.baseUrl}/api/followup-modules/getAllModules`);
            const data = await res.json();
            if (data.modules) {
              modulesToUse = data.modules.sort((a, b) => a.count - b.count);
              setAllModules(modulesToUse);
            }
          }

          // Filter out Patient Onboarding Details (already completed in entry)
          const filteredModules = modulesToUse.filter(
            (m) => !m.module_name?.toLowerCase().includes('patient onboarding')
          );

          // DISABLED: Pain Diagram step hidden
          // setModuleData([...filteredModules, { module_name: 'Pain Diagram', module_type: 'pain_diagram' }]);
          setModuleData(filteredModules);
        } else if (isExistingUser) {
          if (selectedModules.length === 0) {
            navigate('/select');
            return;
          }

          const filteredModules = availableModules
            .filter((m) => {
              const moduleId = m.id || m.module_name?.toLowerCase().replace(/\s+/g, '_');
              return selectedModules.includes(moduleId);
            })
            .sort((a, b) => (a.count || 0) - (b.count || 0));

          // DISABLED: Pain Diagram step hidden
          // const hasPainDiagram = selectedModules.includes('pain_diagram');
          // if (hasPainDiagram) {
          //   setModuleData([...filteredModules, { module_name: 'Pain Diagram', module_type: 'pain_diagram' }]);
          // } else {
          setModuleData(filteredModules);
          // }
        }
        setIsInitialized(true);
      } catch (err) {
        logger.error('Error initializing modules:', err?.message);
      }
    };

    initialize();
  }, [isNewUser, isExistingUser, selectedModules, availableModules, allModules, setAllModules, isInitialized, navigate]);

  // Fetch fields for modules
  useEffect(() => {
    const fetchFieldsForModule = async (moduleName) => {
      if (moduleName.toLowerCase() === 'pain diagram') return [];

      try {
        const res = await fetch(`${environment.baseUrl}/api/followup-modules/getFields/${moduleName}`);
        if (!res.ok) throw new Error(`Failed to fetch fields for ${moduleName}`);
        const data = await res.json();

        setModuleContainerWidths(prev => ({
          ...prev,
          [moduleName.toLowerCase()]: data.containerWidth || 'wide'
        }));

        const processedFields = data.fields?.map(field => {
          if (field.type === 'radio' && field.optionsWithConditional) {
            return {
              ...field,
              optionsWithConditional: field.optionsWithConditional.map((cond, index) => ({
                ...cond,
                optionValue: field.options[index]
              }))
            };
          }
          return field;
        }) || [];

        return processedFields;
      } catch (error) {
        logger.error(`Error fetching fields for module ${moduleName}:`, error?.message);
        return [];
      }
    };

    const fetchAllFields = async () => {
      if (fieldsMapFetched.current || moduleData.length === 0) return;
      fieldsMapFetched.current = true;

      const map = {};
      for (const mod of moduleData) {
        const moduleName = mod.module_name || mod.name;
        if (moduleName && moduleName.toLowerCase() !== 'pain diagram') {
          const fields = await fetchFieldsForModule(moduleName);
          map[moduleName] = fields;
        }
      }

      Object.values(map).forEach(fields => {
        registerFieldLabels(fields);
      });

      setFieldsMap(map);
      // Merge into context so review page can access ALL module fields
      if (setContextFieldsMap) {
        setContextFieldsMap(prev => ({ ...prev, ...map }));
      }
    };

    if (moduleData.length > 0) {
      fetchAllFields();
    }
  }, [moduleData]);

  // Generate module names for progress indicator
  const moduleNames = useMemo(() => {
    const names = ['Patient Information'];

    moduleData.forEach(mod => {
      const name = mod.module_name || '';
      if (name.toLowerCase() === 'pain diagram') {
        names.push('Pain Diagram');
      } else if (name.toLowerCase().includes('medical history')) {
        names.push('Medical History');
      } else if (name.toLowerCase().includes('medication') && !name.toLowerCase().includes('allerg')) {
        names.push('Current Medications');
      } else if (name.toLowerCase().includes('family')) {
        names.push('Family History');
      } else if (name.toLowerCase().includes('pharmacy')) {
        names.push('Pharmacy');
      } else if (name.toLowerCase().includes('allerg')) {
        names.push('Allergies');
      } else if (name.toLowerCase().includes('review')) {
        names.push('Review of Systems');
      } else {
        names.push(name.split(' ').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' '));
      }
    });

    return names;
  }, [moduleData]);

  const totalSteps = moduleNames.length;
  const currentOverallStep = currentStep + 2;

  // Get current module info
  const currentModule = moduleData[currentStep];
  const currentModuleName = currentModule?.module_name || currentModule?.name || '';
  const currentFields = fieldsMap[currentModuleName] || [];
  const isPainDiagramStep = currentModule?.module_type === 'pain_diagram' ||
                           currentModuleName.toLowerCase() === 'pain diagram';

  const isLastStep = currentStep >= moduleData.length - 1;

  // Generate initial values - BLANK for new users, prefilled for existing users
  // Also includes previously saved values from updatedFields for both user types
  const initialValues = useMemo(() => {
    const values = {};

    // Get merged data (previous + updated) for existing users
    // For new users, get just the updated fields (values entered in previous steps)
    const mergedData = getMergedFormData();

    // Debug logging (no PHI in production)
    logger.log('[FollowUpSteps] isExistingUser:', isExistingUser);
    logger.log('[FollowUpSteps] previousData keys:', previousData ? Object.keys(previousData).length : 0);
    logger.log('[FollowUpSteps] mergedData keys:', mergedData ? Object.keys(mergedData).length : 0);
    logger.log('[FollowUpSteps] currentModuleName:', currentModuleName);
    logger.log('[FollowUpSteps] currentFields count:', currentFields.length);

    // First, add all previously saved values from updatedFields
    // This ensures values from previous steps are preserved when navigating back
    Object.keys(mergedData).forEach(key => {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      values[normalizedKey] = mergedData[key];
      values[key] = mergedData[key];
    });

    // Helper function to find value by various key formats
    const findValueInData = (data, baseFieldName) => {
      if (!data || !baseFieldName) return undefined;

      // Normalize the base field name - remove ALL non-alphanumeric characters
      const normalized = baseFieldName.toLowerCase().replace(/[^a-z0-9]/g, '');

      // Try direct lookup first with the exact key
      if (data[baseFieldName] !== undefined && data[baseFieldName] !== '') {
        return data[baseFieldName];
      }

      // Try normalized key
      if (data[normalized] !== undefined && data[normalized] !== '') {
        return data[normalized];
      }

      // Try with underscore variants
      const withUnderscore = baseFieldName.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
      if (data[withUnderscore] !== undefined && data[withUnderscore] !== '') {
        return data[withUnderscore];
      }

      // Try to find in data keys that normalize to the same value
      for (const dataKey of Object.keys(data)) {
        // Normalize the data key the same way
        const normalizedDataKey = dataKey.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (normalizedDataKey === normalized && data[dataKey] !== undefined && data[dataKey] !== '') {
          return data[dataKey];
        }
      }

      return undefined;
    };

    // ========== RESTORE ADDRESS FIELDS ==========
    // Address fields are stored as address_street, address_city, etc. or addressstreet, etc.
    const restoreAddressFields = (baseFieldName) => {
      const addressParts = ['street', 'suite', 'city', 'state', 'country'];
      addressParts.forEach(part => {
        // Try multiple formats
        const formats = [
          `${baseFieldName}_${part}`,           // address_street
          `${baseFieldName}${part}`,            // addressstreet
          `${baseFieldName.toLowerCase()}_${part}`,
          `${baseFieldName.toLowerCase()}${part}`
        ];
        for (const format of formats) {
          const val = findValueInData(mergedData, format);
          if (val !== undefined && val !== '') {
            values[`${baseFieldName}_${part}`] = val;
            values[`${baseFieldName}${part}`] = val;
            break;
          }
        }
      });
    };

    // ========== RESTORE AUTHORIZED CONTACTS ==========
    // Backend stores as authorizedcontact1firstname, authorizedcontact2firstname, etc.
    // Contact type stored as isemergency: 'Yes'/'No' and ismedicalauth: 'Yes'/'No'
    const restoreAuthorizedContacts = (fieldName) => {
      const contacts = [];
      // Check for up to 10 contacts
      for (let i = 1; i <= 10; i++) {
        const prefix = `authorizedcontact${i}`;
        const firstName = findValueInData(mergedData, `${prefix}firstname`);
        const lastName = findValueInData(mergedData, `${prefix}lastname`);
        const email = findValueInData(mergedData, `${prefix}email`);
        const phone = findValueInData(mergedData, `${prefix}phone`);
        const relationship = findValueInData(mergedData, `${prefix}relationship`);
        const otherRelationship = findValueInData(mergedData, `${prefix}otherrelationship`);

        // Backend stores as isemergency: 'Yes'/'No' and ismedicalauth: 'Yes'/'No'
        const isEmergency = findValueInData(mergedData, `${prefix}isemergency`);
        const isMedicalAuth = findValueInData(mergedData, `${prefix}ismedicalauth`);
        // Also try alternative field names
        const isEmergencyContact = findValueInData(mergedData, `${prefix}isemergencycontact`);
        const isAuthorizedMedical = findValueInData(mergedData, `${prefix}isauthorizedmedical`);

        if (firstName || lastName || email || phone) {
          // Parse relationship to extract otherRelationship if stored as "Other - xyz"
          let parsedRelationship = relationship || '';
          let parsedOtherRelationship = otherRelationship || '';
          if (parsedRelationship && parsedRelationship.startsWith('Other - ')) {
            parsedOtherRelationship = parsedRelationship.substring(8);
            parsedRelationship = 'Other';
          }

          contacts.push({
            id: Date.now() + i,
            firstName: firstName || '',
            lastName: lastName || '',
            email: email || '',
            phone: phone || '',
            relationship: parsedRelationship,
            otherRelationship: parsedOtherRelationship,
            isEmergencyContact: isEmergency === 'Yes' || isEmergency === true || isEmergency === 'true' ||
                               isEmergencyContact === true || isEmergencyContact === 'true' || isEmergencyContact === 'Yes',
            isAuthorizedMedical: isMedicalAuth === 'Yes' || isMedicalAuth === true || isMedicalAuth === 'true' ||
                                isAuthorizedMedical === true || isAuthorizedMedical === 'true' || isAuthorizedMedical === 'Yes'
          });
        }
      }
      return contacts.length > 0 ? contacts : null;
    };

    // ========== RESTORE ACCORDION CHECKBOXES (Medical History, ROS) ==========
    // Backend stores aggregated data like: medicalhistory_cardiovascular: { selectedValues: ["High BP"] }
    const restoreAccordionCheckboxes = (field, fieldName) => {
      if (!field.accordionSections) return;

      field.accordionSections.forEach((section, sectionIndex) => {
        const sectionFieldName = `${fieldName}_section_${sectionIndex}`;

        // Find the stored aggregated data for this section
        // Backend stores as: fieldname_sectiontitle (e.g., medicalhistory_cardiovascular)
        const sectionKey = `${fieldName}_${section.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
        const sectionKeyNormalized = sectionKey.replace(/_/g, '');

        let storedSection = findValueInData(mergedData, sectionKey);
        if (!storedSection) {
          storedSection = findValueInData(mergedData, sectionKeyNormalized);
        }

        if (storedSection && storedSection.selectedValues && Array.isArray(storedSection.selectedValues)) {
          // Mark checkboxes as checked based on selectedValues
          section.checkboxes?.forEach((checkbox, checkboxIndex) => {
            const checkboxFieldName = `${sectionFieldName}_checkbox_${checkboxIndex}`;
            const isChecked = storedSection.selectedValues.includes(checkbox.label);
            values[checkboxFieldName] = isChecked;

            // Also restore nested input values if any
            if (isChecked && storedSection.additionalInfo && checkbox.nestedInputs) {
              checkbox.nestedInputs.forEach((nestedInput, inputIndex) => {
                const nestedFieldName = `${checkboxFieldName}_input_${inputIndex}`;
                const additionalKey = `${checkbox.label} - ${nestedInput.label}`;
                if (storedSection.additionalInfo[additionalKey]) {
                  values[nestedFieldName] = storedSection.additionalInfo[additionalKey];
                }
              });
            }
          });
        }
      });
    };

    // ========== RESTORE CONDITIONAL FIELD VALUES ==========
    const restoreConditionalFields = (field, fieldName) => {
      if (!field.optionsWithConditional) return;

      const mainValue = findValueInData(mergedData, fieldName);
      if (!mainValue) return;

      field.optionsWithConditional.forEach((config, optionIndex) => {
        if (!config.conditionalInputs) return;

        const optionValue = config.optionValue || config.label;
        // Check if the main value starts with this option
        if (mainValue === optionValue || (typeof mainValue === 'string' && mainValue.startsWith(`${optionValue} - `))) {
          config.conditionalInputs.forEach((conditionalInput, conditionalIndex) => {
            const conditionalFieldName = `${fieldName}_conditional_${optionIndex}_${conditionalIndex}`;
            // Try to extract the value from the main field if it was stored as "Option - Value"
            if (typeof mainValue === 'string' && mainValue.includes(' - ')) {
              const parts = mainValue.split(' - ');
              if (parts.length > 1) {
                values[conditionalFieldName] = parts.slice(1).join(' - ');
              }
            }
            // Also try to find it directly
            const directValue = findValueInData(mergedData, conditionalFieldName);
            if (directValue) {
              values[conditionalFieldName] = directValue;
            }
          });
        }
      });
    };

    // Initialize fields for current module
    currentFields.forEach((field, index) => {
      const fieldName = getFieldName(field, index);

      // Handle special field types that need data restoration
      if (field.type === 'address') {
        restoreAddressFields(fieldName);
        // Set defaults for any missing address parts
        if (!values[`${fieldName}_street`] && !values[`${fieldName}street`]) values[`${fieldName}_street`] = '';
        if (!values[`${fieldName}_suite`] && !values[`${fieldName}suite`]) values[`${fieldName}_suite`] = '';
        if (!values[`${fieldName}_city`] && !values[`${fieldName}city`]) values[`${fieldName}_city`] = '';
        if (!values[`${fieldName}_state`] && !values[`${fieldName}state`]) values[`${fieldName}_state`] = DEFAULT_STATE;
        if (!values[`${fieldName}_country`] && !values[`${fieldName}country`]) values[`${fieldName}_country`] = 'US';
        return;
      }

      if (field.type === 'authorized_contacts') {
        const restoredContacts = restoreAuthorizedContacts(fieldName);
        if (restoredContacts && restoredContacts.length > 0) {
          values[fieldName] = restoredContacts;
        } else if (!values[fieldName] || !Array.isArray(values[fieldName]) || values[fieldName].length === 0) {
          values[fieldName] = [{
            id: Date.now(),
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            relationship: '',
            otherRelationship: '',
            isEmergencyContact: false,
            isAuthorizedMedical: false
          }];
        }
        return;
      }

      if (field.type === 'accordion') {
        restoreAccordionCheckboxes(field, fieldName);
        return;
      }

      // Restore conditional field values for radio buttons
      if (field.type === 'radio' && field.optionsWithConditional) {
        restoreConditionalFields(field, fieldName);
      }

      // Check if we already have a value for this field (from previous steps or existing data)
      let existingValue = values[fieldName];

      // Try various field name formats to find the value
      if (existingValue === undefined || existingValue === '') {
        existingValue = findValueInData(mergedData, fieldName);
      }
      if (existingValue === undefined || existingValue === '') {
        existingValue = findValueInData(mergedData, field.name);
      }
      if (existingValue === undefined || existingValue === '') {
        existingValue = findValueInData(mergedData, field.labelName);
      }
      if (existingValue === undefined || existingValue === '') {
        existingValue = findValueInData(mergedData, field.label);
      }

      if (existingValue !== undefined && existingValue !== '') {
        values[fieldName] = existingValue;
        return; // Keep the existing value
      }

      // Only set defaults if no existing value found
      switch (field.type) {
        case 'checkbox':
          values[fieldName] = field.options?.length > 0 ? [] : false;
          break;
        case 'multiselect':
          values[fieldName] = [];
          break;
        case 'number':
        case 'range':
          values[fieldName] = '';
          break;
        default:
          values[fieldName] = '';
      }
    });

    // ========== RESTORE UPLOADED IMAGES ==========
    // Check for base64 images stored directly in form fields
    // (These are now stored directly in form_data with their field names)
    const base64ImageFields = Object.keys(mergedData).filter(key => {
      const value = mergedData[key];
      return typeof value === 'string' && value.startsWith('data:image/');
    });

    if (base64ImageFields.length > 0) {
      logger.log('[FollowUpSteps] Found base64 image fields count:', base64ImageFields.length);
    }

    // Also check for Google Drive URLs in _uploadedImages (legacy/async uploads)
    const uploadedImages = mergedData._uploadedImages
      || mergedData._uploadedimages
      || mergedData.uploadedimages
      || mergedData['_uploadedImages'];

    logger.log('[FollowUpSteps] Looking for uploaded images, found:', !!uploadedImages);

    if (uploadedImages && typeof uploadedImages === 'object') {
      Object.entries(uploadedImages).forEach(([fieldName, images]) => {
        logger.log('[FollowUpSteps] Restoring image for field:', fieldName);
        if (Array.isArray(images) && images.length > 0) {
          // Get the first image URL for display
          const firstImage = images[0];
          if (firstImage && firstImage.url) {
            // Set the image URL as the field value
            const normalizedFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
            values[normalizedFieldName] = firstImage.url;
            values[fieldName] = firstImage.url;
            // Also store the full images array
            values[`${normalizedFieldName}_images`] = images;
            values[`${fieldName}_images`] = images;
            logger.log('[FollowUpSteps] Set image URL for', normalizedFieldName);
          }
        }
      });
    }

    // Also check for image references stored by the backend
    // These indicate which fields HAD images, even if _uploadedImages isn't available yet
    const imageReferences = mergedData._imageReferences
      || mergedData._imagereferences
      || mergedData.imagereferences;

    if (imageReferences && typeof imageReferences === 'object') {
      logger.log('[FollowUpSteps] Found image references count:', Object.keys(imageReferences).length);

      // For fields with image references but no uploaded images URL,
      // check if _uploadedImages has the corresponding URL
      Object.entries(imageReferences).forEach(([fieldName, refData]) => {
        if (refData && refData.hasImage) {
          const normalizedFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');

          // Check if we already have a URL from _uploadedImages
          if (!values[normalizedFieldName] && uploadedImages) {
            // Look for matching key in uploadedImages
            const matchingKey = Object.keys(uploadedImages).find(key =>
              key.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedFieldName
            );
            if (matchingKey && uploadedImages[matchingKey]) {
              const images = uploadedImages[matchingKey];
              if (Array.isArray(images) && images.length > 0 && images[0].url) {
                values[normalizedFieldName] = images[0].url;
                values[fieldName] = images[0].url;
                logger.log('[FollowUpSteps] Matched image reference to uploaded image:', normalizedFieldName);
              }
            }
          }

          // Mark field as having a previous image (even if URL not available)
          if (!values[normalizedFieldName]) {
            values[`${normalizedFieldName}_hasImage`] = true;
            values[`${fieldName}_hasImage`] = true;
            logger.log('[FollowUpSteps] Field has image reference (pending upload):', normalizedFieldName);
          }
        }
      });
    }

    // Medication data - use existing from database or create empty
    // Try multiple key formats
    const existingMedicationData = mergedData.medicationdata
      || mergedData.medicationData
      || mergedData.MedicationData
      || values['medicationdata'];

    if (existingMedicationData && typeof existingMedicationData === 'object') {
      values['medicationdata'] = existingMedicationData;
      logger.log('[FollowUpSteps] Restored medicationdata, keys:', Object.keys(existingMedicationData).length);
    } else if (!values['medicationdata']) {
      values['medicationdata'] = {
        uploadedFiles: [],
        cameraFiles: [],
        extractedText: {},
        extractedMedicationsFormatted: '',
        extractedMedicationsCount: 0,
        searchResults: [],
        searchResultsFormatted: '',
        searchResultsCount: 0,
        wizardSelections: [],
        wizardSelectionsFormatted: '',
        wizardSelectionsCount: 0,
        selectedPharmacy: null,
        pharmacyFormatted: '',
        totalMedicationsCount: 0,
        timestamp: null
      };
    }

    // Allergies data - use existing from database or create empty
    const existingAllergiesData = mergedData.allergiesdata
      || mergedData.allergiesData
      || mergedData.AllergiesData
      || values['allergiesdata'];

    if (existingAllergiesData && typeof existingAllergiesData === 'object') {
      values['allergiesdata'] = existingAllergiesData;
      logger.log('[FollowUpSteps] Restored allergiesdata, keys:', Object.keys(existingAllergiesData).length);
    } else if (!values['allergiesdata']) {
      values['allergiesdata'] = {
        uploadedFiles: [],
        extractedTextFromFiles: {},
        VoiceInputResults: [],
        totalAllergiesCount: 0,
        timestamp: null
      };
    }

    // Pharmacy data - use existing from database or create empty
    const existingPharmacyData = mergedData.pharmacydata
      || mergedData.pharmacyData
      || mergedData.PharmacyData
      || values['pharmacydata'];

    if (existingPharmacyData && typeof existingPharmacyData === 'object') {
      values['pharmacydata'] = existingPharmacyData;
      logger.log('[FollowUpSteps] Restored pharmacydata');
    } else if (!values['pharmacydata']) {
      values['pharmacydata'] = {
        selectedPharmacy: null,
        pharmacyFormatted: ''
      };
    }

    // Also set pharmacyformatted if it exists
    if (existingPharmacyData?.pharmacyFormatted) {
      values['pharmacyformatted'] = existingPharmacyData.pharmacyFormatted;
    }

    // Pain levels - keep existing values or set empty
    if (values['currentpainlevel'] === undefined) values['currentpainlevel'] = '';
    if (values['worstpainlevel'] === undefined) values['worstpainlevel'] = '';
    if (values['bestpainlevel'] === undefined) values['bestpainlevel'] = '';

    // Manual medications/allergies - preserve across steps
    if (!values['manualMedications']) values['manualMedications'] = [];
    if (!values['manualAllergies']) values['manualAllergies'] = [];

    return values;
  }, [currentFields, getMergedFormData, isExistingUser, previousData]);

  // Get validation schema
  const getCurrentValidationSchema = () => {
    if (isPainDiagramStep) {
      return Yup.object({});
    }
    if (currentFields.length > 0) {
      return generateYupSchema(currentFields);
    }
    return Yup.object({});
  };

  // Handle medication pharmacy extraction
  const handleMedicationPharmacyExtracted = useCallback((pharmacyDetails, setFieldValue) => {
    setFieldValue('extractedPharmacyDetails', pharmacyDetails);
  }, []);

  // Scroll to first error field
  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField) return;

    setTimeout(() => {
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"], #${firstErrorField}, [for="${firstErrorField}"]`
      );

      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        setTimeout(() => {
          const focusTarget = errorElement.tagName === 'LABEL'
            ? document.getElementById(firstErrorField)
            : errorElement;

          if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus({ preventScroll: true });

            // Highlight the field
            focusTarget.style.outline = '2px solid #ff6b6b';
            focusTarget.style.outlineOffset = '2px';

            setTimeout(() => {
              focusTarget.style.outline = '';
              focusTarget.style.outlineOffset = '';
            }, 3000);
          }
        }, 500);
      }
    }, 100);
  };

  // Handle next step
  const handleNextStep = async (values, validateForm, setTouched) => {
    setStepLoading(true);
    setSubmitError(null);
    setHasValidationError(false);

    try {
      if (!isPainDiagramStep) {
        const formErrors = await validateForm();

        if (Object.keys(formErrors).length > 0) {
          setHasValidationError(true);
          const touchedFields = Object.keys(formErrors).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {});
          setTouched(touchedFields);
          setSubmitError('Please fix validation errors');
          setStepLoading(false);

          // Scroll to first error
          scrollToFirstError(formErrors);
          return;
        }
      }

      updateFields(values);

      if (isLastStep) {
        navigate('/review');
      } else {
        goToNextStep();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setStepLoading(false);
    }
  };

  // Handle back - save current values before navigating
  const handlePrevStep = (values) => {
    // Save current form values before going back
    if (values) {
      updateFields(values);
    }

    if (currentStep === 0) {
      if (isExistingUser) {
        // Go back to module selection for existing users
        navigate('/select');
      } else {
        // Go back to entry for new users
        navigate('/');
      }
    } else {
      goToPreviousStep();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle pain diagram submit
  const handlePainDiagramSubmit = async (diagramPdf) => {
    setPainDiagramPdf(diagramPdf);
    updateFields({ painDiagramPdf: diagramPdf });
    navigate('/review');
  };

  // Render step
  const renderStep = (props) => {
    const { values, errors, touched, setFieldValue } = props;
    const moduleType = currentModule?.module_type;

    const sharedProps = {
      ...props,
      setPainLevels,
      canvasGender,
      setCanvasGender,
      getFieldName,
    };

    if (isPainDiagramStep) {
      // Try to get pain diagram data from multiple sources
      const mergedData = getMergedFormData();
      const drawingsData = painDiagramData
        || mergedData?.painDiagramData
        || mergedData?.paindiagramdata
        || previousData?.painDiagramData
        || previousData?.paindiagramdata;

      logger.log('[FollowUpSteps] Rendering Pain Diagram step, isExistingUser:', isExistingUser, 'hasDrawingsData:', !!drawingsData, 'canvasGender:', canvasGender);

      return (
        <Step5Diagram
          ref={step5Ref}
          onSubmitWithPopup={handlePainDiagramSubmit}
          canvasGender={canvasGender}
          setCanvasGender={setCanvasGender}
          previousDrawings={isExistingUser ? drawingsData : null}
        />
      );
    }

    if (moduleType === 'medication') {
      return (
        <Medication
          moduleData={currentFields}
          {...sharedProps}
          onPharmacyExtracted={(details) => handleMedicationPharmacyExtracted(details, setFieldValue)}
        />
      );
    }

    if (moduleType === 'medication_allergies') {
      return (
        <MedicationAllergies
          moduleData={currentFields}
          {...sharedProps}
          onPharmacyExtracted={(details) => handleMedicationPharmacyExtracted(details, setFieldValue)}
        />
      );
    }

    if (moduleType === 'pharmacy') {
      return (
        <Pharmacy
          moduleData={currentFields}
          {...sharedProps}
          extractedPharmacyDetails={[
            ...(values.extractedPharmacyDetails || []),
            ...(values.extractedPharmacyDetailsAllergies || [])
          ]}
        />
      );
    }

    return (
      <DynamicStepComponent
        {...sharedProps}
        step={currentOverallStep}
        moduleName={currentModuleName}
        moduleFields={currentFields}
        moduleData={currentModule}
        setCanvasGender={setCanvasGender}
      />
    );
  };

  const currentContainerWidth = moduleContainerWidths[currentModuleName.toLowerCase()] || 'wide';

  // Loading state - also wait for sessionReady
  if (!sessionReady || (!isExistingUser && !isNewUser) || !isInitialized) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '40px' }}>
        <div className="skeleton-container">
          <div className="skeleton-header">
            <div className="skeleton skeleton-logo"></div>
          </div>
          <div className="skeleton-form">
            <div className="skeleton skeleton-title"></div>
            <div className="skeleton-row">
              <div className="skeleton-field">
                <div className="skeleton skeleton-label"></div>
                <div className="skeleton skeleton-input"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentModule || (Object.keys(fieldsMap).length === 0 && !isPainDiagramStep)) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '40px' }}>
        <div className="skeleton-container">
          <div className="skeleton-header">
            <div className="skeleton skeleton-logo"></div>
          </div>
          <div className="skeleton-form">
            <div className="skeleton skeleton-title"></div>
            <div className="skeleton-row">
              <div className="skeleton-field">
                <div className="skeleton skeleton-label"></div>
                <div className="skeleton skeleton-input"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Logo - hide on Pain Diagram step */}
      {!isPainDiagramStep && (
        <div className="logo-img d-flex-center">
          <div style={{ textAlign: 'center' }}>
            <img src={logoToDisplay} alt={tenant?.brandName || 'Logo'} style={{ width: '200px', height: 'auto', objectFit: 'contain', marginTop: '10px' }} />
          </div>
        </div>
      )}

      {/* Progress Indicator - hide on Pain Diagram */}
      {!isPainDiagramStep && (
        <StepProgressIndicator
          currentStep={currentOverallStep}
          totalSteps={totalSteps}
          moduleNames={moduleNames}
        />
      )}

      {/* Form Container */}
      <div className={`form-container ${isPainDiagramStep ? 'wide' : currentContainerWidth}`}>
        {submitError && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            padding: '15px',
            margin: '15px 0',
            color: '#c33'
          }}>
            {submitError}
          </div>
        )}

        <Formik
          key={`formik-${currentStep}-${currentModuleName}`}
          initialValues={initialValues}
          validationSchema={getCurrentValidationSchema()}
          onSubmit={() => {}}
          enableReinitialize={true}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, validateForm, setFieldValue, setTouched }) => (
            <Form onSubmit={(e) => e.preventDefault()}>
              {renderStep({ values, errors, touched, setFieldValue })}

              <FormNavigation
                step={currentOverallStep}
                totalSteps={totalSteps}
                prevStep={() => handlePrevStep(values)}
                nextStep={
                  isPainDiagramStep
                    ? () => {
                        // For pain diagram step, show the popup
                        if (step5Ref.current?.showPopup) {
                          step5Ref.current.showPopup();
                        }
                      }
                    : () => handleNextStep(values, validateForm, setTouched)
                }
                loading={stepLoading}
                isLoading={stepLoading}
                formValid={Object.keys(errors).length === 0}
                hasError={hasValidationError}
              />
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default FollowUpSteps;
